import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  listCampaigns,
  launchCampaign as launchCampaignAPI,
  cancelCampaign as cancelCampaignAPI,
  getCampaign,
  getCampaignSummary,
  getCampaignDetailSummary,
  listCampaignSubjects,
} from "adapters/campaign.service";

const initialState = {
  list: { items: [], nextPageToken: null },
  byId: {},
  launch: { status: "idle", taskId: null, links: null, error: null },
  cancel: { status: "idle", error: null },
  summary: { data: null, loading: false, error: null },
  detailSummary: { byId: {}, loading: false, error: null },
  loading: { list: false, byId: false, launch: false, cancel: false },
  error: { list: null, byId: null, launch: null, cancel: null },
};

const extractError = (err) =>
  err?.response?.data?.message || err?.message || "Unknown error";

export const fetchCampaignList = createAsyncThunk(
  "campaign/fetchCampaignList",
  async ({ filters = {}, pageToken, pageSize } = {}, { rejectWithValue }) => {
    try {
      return await listCampaigns(filters, pageToken, pageSize);
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const launchCampaign = createAsyncThunk(
  "campaign/launchCampaign",
  async ({ payload }, { rejectWithValue }) => {
    try {
      const r = await launchCampaignAPI(payload);
      const taskId = r?.taskId;
      if (!taskId || typeof taskId !== "string") {
        return rejectWithValue({
          stage: "submit",
          message: "Launch response missing taskId",
        });
      }
      return { taskId };
    } catch (err) {
      return rejectWithValue({
        stage: "submit",
        message: extractError(err),
        details: err?.response?.data || null,
      });
    }
  }
);

export const fetchCampaignById = createAsyncThunk(
  "campaign/fetchCampaignById",
  async (id, { rejectWithValue }) => {
    try {
      return await getCampaign(id);
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const cancelCampaign = createAsyncThunk(
  "campaign/cancelCampaign",
  async (id, { rejectWithValue }) => {
    try {
      return await cancelCampaignAPI(id);
    } catch (err) {
      if (err?.response?.status === 409) {
        return rejectWithValue({
          kind: "already-cancelled",
          message: err.response.data?.error,
        });
      }
      return rejectWithValue({ kind: "error", message: extractError(err) });
    }
  }
);

export const fetchCampaignSummary = createAsyncThunk(
  "campaign/fetchCampaignSummary",
  async (_arg, { rejectWithValue }) => {
    try {
      return await getCampaignSummary();
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const fetchCampaignDetailSummary = createAsyncThunk(
  "campaign/fetchCampaignDetailSummary",
  async (compositionId, { rejectWithValue }) => {
    try {
      return { compositionId, data: await getCampaignDetailSummary(compositionId) };
    } catch (err) {
      return rejectWithValue({ compositionId, error: extractError(err) });
    }
  }
);

export const fetchCampaignSubjects = createAsyncThunk(
  "campaign/fetchCampaignSubjects",
  async ({ compositionId, filters, pageToken, pageSize }, { rejectWithValue }) => {
    try {
      const { items = [], nextPageToken } = await listCampaignSubjects(
        compositionId,
        filters,
        pageToken,
        pageSize
      );
      const norm = items.map(({ "mia-sent-at": miaSentAt, ...rest }) => ({
        ...rest,
        miaSentAt: miaSentAt ?? null,
      }));
      return { items: norm, nextPageToken: nextPageToken || null };
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  }
);

const slice = createSlice({
  name: "campaign",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCampaignList.pending, (state) => {
        state.loading.list = true;
        state.error.list = null;
      })
      .addCase(fetchCampaignList.fulfilled, (state, action) => {
        state.loading.list = false;
        state.list = action.payload;
      })
      .addCase(fetchCampaignList.rejected, (state, action) => {
        state.loading.list = false;
        state.error.list = action.payload;
      })
      .addCase(launchCampaign.pending, (state) => {
        state.launch = {
          status: "submitting",
          taskId: null,
          links: null,
          error: null,
        };
        state.loading.launch = true;
      })
      .addCase(launchCampaign.fulfilled, (state, action) => {
        state.launch = {
          status: "succeeded",
          taskId: action.payload.taskId,
          error: null,
        };
        state.loading.launch = false;
      })
      .addCase(launchCampaign.rejected, (state, action) => {
        state.launch = {
          status: "failed",
          taskId: action.payload?.taskId || null,
          links: null,
          error: action.payload?.message || "Launch failed",
        };
        state.loading.launch = false;
      })
      .addCase(fetchCampaignById.pending, (state) => {
        state.loading.byId = true;
        state.error.byId = null;
      })
      .addCase(fetchCampaignById.fulfilled, (state, action) => {
        state.byId[action.payload.campaignConfig.id] = action.payload;
        state.loading.byId = false;
      })
      .addCase(fetchCampaignById.rejected, (state, action) => {
        state.loading.byId = false;
        state.error.byId = action.payload;
      })
      .addCase(cancelCampaign.pending, (state) => {
        state.cancel = {
          status: "submitting",
          error: null,
          cleanupTaskId: null,
        };
      })
      .addCase(cancelCampaign.fulfilled, (state, action) => {
        state.cancel = {
          status: "succeeded",
          error: null,
          cleanupTaskId: action.payload.cleanupTaskId,
        };
      })
      .addCase(cancelCampaign.rejected, (state, action) => {
        const status =
          action.payload?.kind === "already-cancelled"
            ? "already-cancelled"
            : "failed";
        state.cancel = {
          status,
          error: action.payload?.message || null,
          cleanupTaskId: null,
        };
      })
      .addCase(fetchCampaignSummary.pending, (state) => {
        state.summary.loading = true;
        state.summary.error = null;
      })
      .addCase(fetchCampaignSummary.fulfilled, (state, action) => {
        state.summary.loading = false;
        state.summary.data = action.payload;
      })
      .addCase(fetchCampaignSummary.rejected, (state, action) => {
        state.summary.loading = false;
        state.summary.error = action.payload;
      })
      .addCase(fetchCampaignDetailSummary.pending, (state) => {
        state.detailSummary.loading = true;
        state.detailSummary.error = null;
      })
      .addCase(fetchCampaignDetailSummary.fulfilled, (state, action) => {
        state.detailSummary.loading = false;
        state.detailSummary.byId[action.payload.compositionId] = action.payload.data;
      })
      .addCase(fetchCampaignDetailSummary.rejected, (state, action) => {
        state.detailSummary.loading = false;
        state.detailSummary.error = action.payload?.error || "Unknown error";
      });
  },
});

export const selectCampaignById = (s, id) => s.campaign.byId[id] || null;
export const selectCampaignList = (s) => s.campaign.list;
export const selectListLoading = (s) => s.campaign.loading.list;
export const selectListError = (s) => s.campaign.error.list;
export const selectLaunchState = (s) => s.campaign.launch;
export const selectCancelState = (s) => s.campaign.cancel;
export const selectCampaignSummary = (s) => s.campaign.summary;
export const selectCampaignDetailSummary = (s, compositionId) => ({
  data: s.campaign.detailSummary.byId[compositionId] || null,
  loading: s.campaign.detailSummary.loading,
  error: s.campaign.detailSummary.error,
});

export default slice.reducer;
